import styled from "@emotion/styled"
import Link from "next/link"

// ============ Section & Loading ============

export const StyledSection = styled.section`
  width: 100%;
  display: flex;
  height: 558px;
  position: relative;
  ${(props) => props.theme.breakpoints.up("md")} {
    height: 800px;
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    height: 1200px;
  }
`

export const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 10;
`

export const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid #e0e0e0;
  border-top-color: #2768a3;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`

// ============ Slider Container ============

export const StyledPortSlider = styled.div`
  position: absolute;
  inset-inline-start: 0;
  z-index: 100;

  display: flex;
  flex-direction: column;

  bottom: 11px;
  width: 100%;
  padding-inline: 15px;
  overflow: hidden;
  min-height: 250px;

  ${(props) => props.theme.breakpoints.up("md")} {
    box-shadow: 0 0 17.3px 0 rgba(114, 111, 111, 0.2);
    background: white;
    border-radius: 24px;
    bottom: unset;
    width: 480px;
    height: calc(100% - 64px);
    top: 32px;
    inset-inline-start: 60px;
    padding: 0;
    min-height: unset;
  }

  ${(props) => props.theme.breakpoints.up("xl")} {
    top: 70px;
    height: calc(100% - 140px);
    inset-inline-start: 120px;
  }
`

export const StyledPortSliderHeader = styled.h5`
  display: none;
  margin: 0;
  padding: 8px 16px;
  color: ${({ theme }) => theme.colors.port.defaultTextColor};
  font-size: 40px;
  font-weight: 400;
  line-height: 125%;
  letter-spacing: -0.8px;
  text-transform: capitalize;
  position: relative;
  z-index: 2;
  box-shadow: 0 0 17.3px 0 rgba(114, 111, 111, 0.2);

  ${(props) => props.theme.breakpoints.up("md")} {
    display: block;
  }
`

export const StyledSliderWrapper = styled.div`
  display: flex;
  width: 100%;
  position: relative;
  z-index: 1;

  ${(props) => props.theme.breakpoints.up("md")} {
    overflow: hidden;
    flex: 1;
    height: 100%;
  }

  .swiper {
    padding-block: 0 !important;
  }

  .swiper,
  swiper-wrapper {
    overflow: visible !important;
  }

  .swiper-slide {
    width: 100% !important;
  }

  /* Desktop - hide native scrollbar, we use custom one */
  ${(props) => props.theme.breakpoints.up("md")} {
    .swiper {
      overflow-y: scroll !important;
      padding-inline-end: 20px;

      /* Hide native scrollbar */
      scrollbar-width: none;
      -ms-overflow-style: none;

      &::-webkit-scrollbar {
        display: none;
        width: 0;
        height: 0;
      }
    }
  }
`

// Custom scrollbar components
export const CustomScrollbarTrack = styled.div`
  display: none;

  ${(props) => props.theme.breakpoints.up("md")} {
    display: block;
    position: absolute;
    inset-inline-start: 0;
    top: -2px;
    bottom: 0;
    width: 6px;
    background: #e8e8e8;
    border-radius: 3px;
    z-index: 10;
  }
`

export const CustomScrollbarThumb = styled.div<{
  $thumbHeight: number
  $thumbTop: number
  $isDragging?: boolean
}>`
  display: none;

  ${(props) => props.theme.breakpoints.up("md")} {
    display: block;
    position: absolute;
    width: 100%;
    height: ${(props) => props.$thumbHeight}%;
    top: ${(props) => props.$thumbTop}%;
    background-color: ${(props) => props.theme.colors.port.defaultTextColor};
    border-radius: 3px;
    cursor: ${(props) => (props.$isDragging ? "grabbing" : "grab")};
    transition: ${(props) => (props.$isDragging ? "none" : "background-color 0.2s ease")};
    min-height: 40px;
    user-select: none;

    &:hover {
      background-color: ${(props) => props.theme.colors.port.defaultTextColor};
      opacity: 0.8;
    }

    &:active {
      cursor: grabbing;
    }
  }
`

// ============ Mobile Card Styles ============

export const MobileCard = styled.div<{ $direction: "ltr" | "rtl"; $active?: boolean }>`
  border-radius: 8px;
  background: #fff;
  color: black;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  box-shadow: 0 0 17.3px 0 rgba(114, 111, 111, 0.2);
  direction: ${(props) => props.$direction};
  transition: all 0.3s ease;
  background-color: ${(props) => (props.$active ? "rgba(0, 0, 250, 0.05)" : "#fff")};
`

export const MobileImageWrapper = styled.div`
  width: 100%;
  height: 72px;
  position: relative;
  border-radius: 4px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

export const MobileCardBody = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  position: relative;
`

export const MobileInfoColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`

// ============ Desktop Card Styles ============

export const DesktopCard = styled.div<{ $direction: "ltr" | "rtl"; $active?: boolean }>`
  display: flex;
  gap: 16px;
  padding: 16px 12px;
  height: 251px;
  border-bottom: 1px solid ${(props) => props.theme.colors.port.grey};
  transition: all 0.3s ease;
  background-color: ${(props) => (props.$active ? "rgba(0, 0, 250, 0.05)" : "transparent")};
  &:hover {
    background-color: rgba(0, 0, 250, 0.05);
  }
`

export const DesktopImageWrapper = styled.div`
  width: 190px;
  height: 100%;
  position: relative;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

export const DesktopContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 1px;
  color: black;
`

export const DesktopHeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  position: relative;
`

export const DesktopTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

// ============ Shared Card Elements ============

export const WazeButton = styled(Link)<{ $isDisabled?: boolean }>`
  width: 30px;
  height: 30px;
  min-width: 30px;
  border-radius: 50%;
  border: 1px solid ${(props) => props.theme.colors.port.defaultTextColor};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.theme.colors.port.defaultTextColor};
  text-decoration: none;
  background-color: white;
  transition: all 0.2s ease;
  cursor: ${(props) => (props.$isDisabled ? "default" : "pointer")};
  opacity: ${(props) => (props.$isDisabled ? 0.35 : 1)};
  pointer-events: ${(props) => (props.$isDisabled ? "none" : "auto")};
  position: absolute;
  inset-inline-end: 0;
  top: 0;
`

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding-inline-end: 30px;
  min-width: 0;
  svg {
    flex-shrink: 0;
  }
`

export const PortTitle = styled.h5`
  margin: 0;
  padding: 0;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  overflow: hidden;
  text-overflow: ellipsis;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 20px;
  }
`

export const AddressText = styled.p`
  margin: 0;
  padding: 0;
  font-size: 12px;
  font-weight: 400;
  line-height: 150%;
  margin-inline-start: 20px;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 16px;
  }
`

export const InfoBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 4px;
  position: relative;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 8px;
  }
`

export const InfoRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  color: ${(props) => props.theme.colors.port.defaultTextColor};
  svg {
    margin-top: 4px;
  }
`

export const InfoLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 140%;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 18px;
  }
`

export const BusLines = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-inline-start: 20px;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 12px;
  }
`

export const BusBadge = styled.span`
  min-width: 20px;
  height: 20px;
  padding-inline: 3.5px;
  border-radius: 4px;
  border: 1px solid ${(props) => props.theme.colors.port.defaultTextColor};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.theme.colors.port.defaultTextColor};
  background: white;
  box-sizing: border-box;

  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 16px;
    min-width: 24px;
    height: 24px;
    padding-inline: 4.5px;
  }
`

export const SecondaryText = styled.p`
  margin: 0;
  font-size: 12px;
  font-weight: 400;
  line-height: 150%;
  margin-inline-start: 20px;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 16px;
  }
`
